<template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'Account Details'">
      <template #left>
        <font-awesome-icon
          @click="
            router.push({
              name: `${router.currentRoute.value.params.previousPage}`,
            })
          "
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
        />
      </template>
    </MainHeader>

    <div class="view ea-view">
      <div class="et-tabs">
        <button
          class="et-tab"
          :class="{ active: !editCategorySliderSelected }"
          @click="editCategorySliderSelected = false"
        >
          Info
        </button>
        <button
          class="et-tab"
          :class="{ active: editCategorySliderSelected }"
          @click="editCategorySliderSelected = true"
        >
          Edit
        </button>
      </div>

      <div v-if="!editCategorySliderSelected">
        <div class="ec-hero">
          <div
            class="ec-hero-icon"
            :style="{
              background: (store.selectedAccount.iconColor || '#7F77DD') + '22',
            }"
          >
            <font-awesome-icon
              v-if="store.selectedAccount.icon"
              :icon="parseIconString(store.selectedAccount.icon)"
              :style="{ color: store.selectedAccount.iconColor }"
              style="font-size: 28px"
            />
            <font-awesome-icon
              v-else
              :icon="['fas', 'building-columns']"
              style="font-size: 28px; color: #7f77dd"
            />
          </div>
          <h2 class="ec-hero-name">{{ store.selectedAccount.displayName }}</h2>
          <p class="ec-hero-sub">{{ store.selectedAccount.institutionName }}</p>
        </div>

        <div class="wrapper-card ec-info-card">
          <div class="ud-section">
            <span class="section-label">Account details</span>
            <div class="et-info-row">
              <span class="et-info-label">Account Name</span>
              <span class="et-info-value">{{
                store.selectedAccount.displayName
              }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Institution</span>
              <span class="et-info-value">{{
                store.selectedAccount.institutionName
              }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Account Type</span>
              <span class="et-info-value">{{
                store.selectedAccount.type
              }}</span>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Balances</span>
            <div class="et-info-row">
              <span class="et-info-label">Available Balance</span>
              <span class="et-info-value">{{
                formatMoney(store.selectedAccount.availableBalance)
              }}</span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Current Balance</span>
              <span class="et-info-value">{{
                formatMoney(store.selectedAccount.currentBalance)
              }}</span>
            </div>
          </div>

          <div class="ud-section">
            <span class="section-label">Appearance</span>
            <div class="et-info-row">
              <span class="et-info-label">Icon</span>
              <span class="et-info-value">
                <font-awesome-icon
                  v-if="store.selectedAccount.icon"
                  :icon="parseIconString(store.selectedAccount.icon)"
                  :style="{ color: store.selectedAccount.iconColor }"
                />
                <span
                  v-else
                  style="color: #aaaaaa; font-style: italic; font-size: 0.78rem"
                  >None</span
                >
              </span>
            </div>
            <div class="et-info-row">
              <span class="et-info-label">Color</span>
              <span class="et-info-value">
                <span
                  v-if="store.selectedAccount.iconColor"
                  class="color-dot"
                  :style="{ background: store.selectedAccount.iconColor }"
                ></span>
                <span
                  v-else
                  style="color: #aaa; font-style: italic; font-size: 0.78rem"
                  >None</span
                >
              </span>
            </div>
          </div>
        </div>

        <button class="delete-btn mt-3 w-100" @click="removeAccount">
          Remove Account
        </button>
      </div>

      <div v-else>
        <form @submit.prevent="updateAccount">
          <fieldset>
            <div class="ud-section">
              <span class="section-label">Details</span>

              <div class="ud-field">
                <label class="ud-label"
                  >Account Name <span class="ud-asterisk">*</span></label
                >
                <input
                  type="text"
                  class="ud-input"
                  v-model="accountName"
                  required
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label"
                  >Institution <span class="ud-asterisk">*</span></label
                >
                <input
                  type="text"
                  class="ud-input"
                  v-model="institutionName"
                  :disabled="store.selectedAccount.externalAccountID"
                  required
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label"
                  >Account Type <span class="ud-asterisk">*</span></label
                >
                <select
                  v-model="accountType"
                  class="ud-input ud-select form-select"
                  :disabled="store.selectedAccount.externalAccountID"
                  required
                >
                  <option value="" disabled selected>
                    Select account type
                  </option>
                  <option
                    v-for="(type, key) in accountTypesList"
                    :key="key"
                    :value="type"
                    :selected="type == store.selectedAccount.type"
                  >
                    {{ type }}
                  </option>
                </select>
              </div>
            </div>

            <div class="ud-section">
              <span class="section-label">Balances</span>

              <div class="ud-field">
                <label class="ud-label"
                  >Available Balance <span class="ud-asterisk">*</span></label
                >
                <input
                  type="text"
                  class="ud-input"
                  v-model="availableBalance"
                  :disabled="store.selectedAccount.externalAccountID"
                  required
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label"
                  >Current Balance <span class="ud-asterisk">*</span></label
                >
                <input
                  type="text"
                  class="ud-input"
                  v-model="currentBalance"
                  :disabled="store.selectedAccount.externalAccountID"
                  required
                />
              </div>
            </div>

            <div class="ud-section">
              <span class="section-label">Appearance</span>

              <div class="ud-field">
                <label class="ud-label">Account Icon</label>
                <IconSearch
                  button-text="Choose Icon"
                  styleClass="form-dropdown"
                  :options="
                    store.selectedAccount.icon ? store.selectedAccount.icon : ''
                  "
                  @optionSelected="iconSelected($event)"
                />
              </div>
              <div class="ud-field mt">
                <label class="ud-label">Account Color</label>
                <ColorSelection
                  button-text="Choose Color"
                  styleClass="form-dropdown"
                  :options="
                    store.selectedAccount.iconColor
                      ? store.selectedAccount.iconColor
                      : ''
                  "
                  @optionSelected="iconColorSelected($event)"
                />
              </div>
            </div>

            <button class="ud-submit" type="submit">Update Account</button>
          </fieldset>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { ref, reactive, onMounted } from 'vue';
import MainHeader from '../components/View-Header.vue';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import IconSearch from '../components/Icon-Selector.vue';
import ColorSelection from '../components/Color-Selection.vue';
import { formatMoney } from '../utils/formatMoney.js';
import { Account_Types } from '../utils/constants.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  components: { MainHeader, IconSearch, ColorSelection, SkeletonLoader },
  props: ['id'],
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const searchTerm = ref('');
    const searchResults = reactive({});
    let showAutoCategorization = ref(false);
    let showEditSubcategories = ref(false);
    let editCategorySliderSelected = ref(false);
    const accountIcon = ref(store.selectedAccount.icon);
    const accountColor = ref(store.selectedAccount.iconColor);
    const accountName = ref(store.selectedAccount.displayName);
    const institutionName = ref(store.selectedAccount.institutionName);
    const availableBalance = ref(store.selectedAccount.availableBalance);
    const currentBalance = ref(store.selectedAccount.currentBalance);
    const accountType = ref(store.selectedAccount.type);
    const accountId = ref(store.selectedAccount.id);
    const accountTypesList = ref(Account_Types);
    const loading = ref(true);
    let loadingText = ref('Loading...');

    function searchIcons() {
      if (searchTerm.value.length < 3) return;
      const results = findIconDefinition({
        prefix: '',
        iconName: searchTerm.value,
      });
      searchResults.value = results;
    }

    const iconSelected = (icon) => {
      accountIcon.value = icon;
    };
    const iconColorSelected = (color) => {
      accountColor.value = color;
    };

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const updateAccount = () => {
      loadingText.value = 'Saving Account...';
      loading.value = true;
      try {
        const accountData = {
          AvailableBalance: parseFloat(availableBalance.value),
          CurrentBalance: parseFloat(currentBalance.value),
          Name: accountName.value,
          DisplayName: accountName.value,
          InstitutionName: institutionName.value,
          AccountIcon: accountIcon.value,
          AccountIconColor: accountColor.value,
          AccountType: accountType.value,
          Id: accountId.value,
        };
        store.saveCustomAccount(accountData).then(async () => {
          await store.getPlaidAccountsList();
          loading.value = false;
          router.push({ name: 'ConnectedAccounts' });
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    const removeAccount = async () => {
      loadingText.value = 'Removing Account...';
      loading.value = true;
      try {
        const accountData = { ID: store.selectedAccount.id };
        await store.removeAccount(accountData);
        await router.push({ name: 'ConnectedAccounts' });
      } catch (error) {
        console.error('An error occurred:', error);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) mainElement.scrollTo(0, 0);
      }, 100);
      loading.value = false;
    });

    return {
      store,
      router,
      searchIcons,
      searchTerm,
      searchResults,
      showAutoCategorization,
      showEditSubcategories,
      editCategorySliderSelected,
      formatMoney,
      iconSelected,
      accountIcon,
      iconColorSelected,
      accountColor,
      parseIconString,
      updateAccount,
      accountName,
      institutionName,
      availableBalance,
      currentBalance,
      accountType,
      removeAccount,
      accountTypesList,
      loading,
      loadingText,
    };
  },
};
</script>

<style scoped>
.ea-view {
  max-width: 480px;
  margin: 0 auto;
}

.et-tabs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 1.5rem;
  background: #f0f0f4;
  border-radius: var(--radius-pill);
  padding: 4px;
}
.dark-theme .et-tabs {
  background: #1a2535;
}

.et-tab {
  border: none;
  border-radius: var(--radius-pill);
  padding: 0.6rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  background: transparent;
  color: #888;
  transition: all 0.2s ease;
}
.et-tab.active {
  background: linear-gradient(135deg, #7f77dd, #5dcaa5);
  color: white;
  box-shadow: 0 4px 12px rgba(127, 119, 221, 0.3);
}

.ec-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem 0 1.25rem;
  animation: slideUp 0.4s ease both;
}
.ec-hero-icon {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both;
}
.ec-hero-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 2px;
  letter-spacing: -0.02em;
  text-align: center;
}
.dark-theme .ec-hero-name {
  color: white;
}
.ec-hero-sub {
  font-size: 0.78rem;
  color: #aaa;
  margin: 0;
}

.ec-info-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
}

.et-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .et-info-row {
  border-bottom-color: #2a3a4e;
}
.et-info-row:last-child {
  border-bottom: none;
}
.et-info-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #888;
}
.et-info-value {
  font-size: 0.82rem;
  font-weight: 500;
  color: #213547;
  text-align: right;
}
.dark-theme .et-info-value {
  color: #d4d2d2;
}

.color-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.delete-btn {
  border: 2px solid #d85a30;
  background: white;
  color: #d85a30;
  border-radius: var(--radius-pill);
  padding: 0.75rem;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.15s;
}
.delete-btn:hover {
  background: #faece7;
  transform: translateY(-1px);
}
.delete-btn:active {
  transform: scale(0.97);
}
.dark-theme .delete-btn {
  background: transparent;
  color: #f09595;
  border-color: #f09595;
}
</style>

<!-- <template>
  <div v-if="loading" class="skeleton-loader-wrapper">
    <SkeletonLoader :loadingText="loadingText" />
  </div>
  <div v-else>
    <MainHeader :title="'Account Details'">
      <template #left>
        <font-awesome-icon
          @click="
            router.push({
              name: `${router.currentRoute.value.params.previousPage}`,
            })
          "
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
        />
      </template>
    </MainHeader>
    <div class="view edit-account-view">
      <div>
        <Slider
          :options="sliderOptions"
          @sliderSelection="sliderOptionUpdated($event)"
        ></Slider>
      </div>
      <div class="add-account-form" v-if="editCategorySliderSelected">
        <div class="text-center pt-4 pb-4">
          <font-awesome-icon
            :icon="['fas', 'folder-open']"
            class="view-icon info-icon"
          />
        </div>
        <form @submit.prevent="updateAccount">
          <fieldset>
            <div class="mt-4">
              <label class="small-grey-text" for="accountName"
                >Account Name *</label
              >
              <input type="text" class="input" v-model="accountName" required />
            </div>
            <div class="mt-1">
              <label class="small-grey-text" for="institution"
                >Institution *</label
              >
              <input
                id="institution"
                type="text"
                class="input"
                v-model="institutionName"
                :disabled="store.selectedAccount.externalAccountID"
                required
              />
            </div>
            <div class="mt-1">
              <label class="small-grey-text" for="availableBalance"
                >Available Balance *</label
              >
              <input
                id="availableBalance"
                type="text"
                class="input"
                v-model="availableBalance"
                :disabled="store.selectedAccount.externalAccountID"
                required
              />
            </div>
            <div class="mt-1">
              <label class="small-grey-text" for="currentBalance"
                >Current Balance *</label
              >
              <input
                id="currentBalance"
                type="text"
                class="input"
                v-model="currentBalance"
                :disabled="store.selectedAccount.externalAccountID"
                required
              />
            </div>
            <div class="mt-1">
              <label for="accountType" class="small-grey-text"
                >Account Type *</label
              >
              <select
                v-model="accountType"
                class="input form-select white-form-select"
                :disabled="store.selectedAccount.externalAccountID"
                required
              >
                <option value="" disabled selected>Select Account Type</option>
                <option
                  v-for="(type, key) in accountTypesList"
                  :key="key"
                  :value="type"
                  :selected="type == store.selectedAccount.type"
                >
                  {{ type }}
                </option>
              </select>
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Account Icon</label>
              <IconSearch
                button-text="Choose Icon"
                styleClass="form-dropdown"
                :options="
                  store.selectedAccount.icon ? store.selectedAccount.icon : ''
                "
                @optionSelected="iconSelected($event)"
              />
            </div>
            <div class="mt-1">
              <label class="small-grey-text">Account Icon Color</label>
              <ColorSelection
                button-text="Choose Color"
                styleClass="form-dropdown"
                :options="
                  store.selectedAccount.iconColor
                    ? store.selectedAccount.iconColor
                    : ''
                "
                @optionSelected="iconColorSelected($event)"
              ></ColorSelection>
            </div>
            <button class="blue-button mt-4 w-100" type="submit">
              Update Account
            </button>
          </fieldset>
        </form>
      </div>
      <div v-else>
        <div class="text-center pt-4 pb-4">
          <font-awesome-icon
            :icon="['fas', 'folder']"
            class="view-icon info-icon"
          />
        </div>
        <div class="cat-info-card">
          <div class="mt-1">
            <label for="accountName" class="small-grey-text"
              >Account Name</label
            >
            <p id="accountName">
              {{ store.selectedAccount.displayName }}
            </p>
          </div>
          <div class="mt-1">
            <label for="institution" class="small-grey-text">Institution</label>
            <p id="institution">
              {{ store.selectedAccount.institutionName }}
            </p>
          </div>
          <div class="mt-1">
            <label for="accountBalance" class="small-grey-text"
              >Account Balance</label
            >
            <p id="accountBalance">
              {{ formatMoney(store.selectedAccount.availableBalance) }}
            </p>
          </div>
          <div class="mt-1">
            <label for="currentBalance" class="small-grey-text"
              >Current Balance</label
            >
            <p id="currentBalance">
              {{ formatMoney(store.selectedAccount.currentBalance) }}
            </p>
          </div>
          <div class="mt-1">
            <label for="accountType" class="small-grey-text"
              >Account Type</label
            >
            <p id="accountType">
              {{ store.selectedAccount.type }}
            </p>
          </div>
          <div class="mt-1">
            <label for="accountIcon" class="small-grey-text"
              >Account Icon</label
            >
            <p id="accountIcon" v-if="store.selectedAccount.icon">
              <font-awesome-icon
                :icon="parseIconString(store.selectedAccount.icon)"
              />
            </p>
            <p id="accountIcon" v-else>No icon selected</p>
          </div>
          <div class="mt-1">
            <label for="accountIconColor" class="small-grey-text"
              >Account Icon Color</label
            >
            <div
              v-if="store.selectedAccount.iconColor"
              id="accountIconColor"
              class="category-color"
              :style="{ background: store.selectedAccount.iconColor }"
            ></div>
            <div v-else id="accountIconColor">No color selected</div>
          </div>
        </div>
        <button class="red-button mt-4 w-100" @click="removeAccount">
          Remove Account
        </button>
      </div>
    </div>
  </div>
</template>
<script>
import { useMyStore } from '../store';
import { useRouter } from 'vue-router';
import { ref, reactive, onMounted } from 'vue';

import MainHeader from '../components/View-Header.vue';
import Slider from '../components/Slider.vue';
import { findIconDefinition } from '@fortawesome/fontawesome-svg-core';
import IconSearch from '../components/Icon-Selector.vue';
import ColorSelection from '../components/Color-Selection.vue';
import { Edit_Category_Types } from '../utils/constants.js';
import { formatMoney } from '../utils/formatMoney.js';
import { Account_Types } from '../utils/constants.js';
import SkeletonLoader from '../components/SkeletonLoader.vue';

export default {
  components: {
    MainHeader,
    Slider,
    IconSearch,
    ColorSelection,
    SkeletonLoader,
  },
  props: ['id'],
  setup() {
    const store = useMyStore();
    const router = useRouter();
    const sliderOptions = [
      { label: 'Info', disabled: false },
      { label: 'Edit', disabled: true },
    ];
    const searchTerm = ref('');
    const searchResults = reactive({});
    let showAutoCategorization = ref(false);
    let showEditSubcategories = ref(false);
    let editCategorySliderSelected = ref(false);
    const accountIcon = ref(store.selectedAccount.icon);
    const accountColor = ref(store.selectedAccount.iconColor);
    const accountName = ref(store.selectedAccount.displayName);
    const institutionName = ref(store.selectedAccount.institutionName);
    const availableBalance = ref(store.selectedAccount.availableBalance);
    const currentBalance = ref(store.selectedAccount.currentBalance);
    const accountType = ref(store.selectedAccount.type);
    const accountId = ref(store.selectedAccount.id);
    const accountTypesList = ref(Account_Types);
    const loading = ref(true);
    let loadingText = ref('Loading...');

    function searchIcons() {
      if (searchTerm.value.length < 3) {
        return;
      }
      const results = findIconDefinition({
        prefix: '',
        iconName: searchTerm.value,
      });
      searchResults.value = results;
    }

    const sliderOptionUpdated = (selection) => {
      editCategorySliderSelected.value = selection === Edit_Category_Types.edit;
    };

    const addSubcategory = () => {
      store.updateAddSubcategoryToEditCategory(true);
      router.push({ name: 'AddCategory' });
    };

    const iconSelected = (icon) => {
      accountIcon.value = icon;
    };

    const iconColorSelected = (color) => {
      accountColor.value = color;
    };

    const parseIconString = (iconString) => {
      if (iconString) {
        const [category, icon] = iconString.split(' ');
        return [category, icon];
      }
      return null;
    };

    const updateAccount = () => {
      loadingText.value = 'Saving Account...';
      loading.value = true;

      try {
        const accountData = {
          AvailableBalance: parseFloat(availableBalance.value),
          CurrentBalance: parseFloat(currentBalance.value),
          Name: accountName.value,
          DisplayName: accountName.value,
          InstitutionName: institutionName.value,
          AccountIcon: accountIcon.value,
          AccountIconColor: accountColor.value,
          AccountType: accountType.value,
          Id: accountId.value,
        };
        store.saveCustomAccount(accountData).then(() => {
          loading.value = false;
          router.push({ name: 'ConnectedAccounts' });
        });
      } catch (error) {
        loading.value = false;
        console.error('An error occurred:', error);
      }
    };

    const removeAccount = async () => {
      loadingText.value = 'Removing Account...';
      loading.value = true;

      try {
        const accountData = {
          ID: store.selectedAccount.id,
        };
        await store.removeAccount(accountData);
        await router.push({ name: 'ConnectedAccounts' });
      } catch (error) {
        console.error('An error occurred:', error);
      } finally {
        loading.value = false;
      }
    };

    // const removeAccount = () => {
    //   loadingText.value = 'Removing Account...';
    //   loading.value = true;
    //   try {
    //     const accountData = {
    //       ID: store.selectedAccount.id,
    //     };
    //     store
    //       .removeAccount(accountData)
    //       .then(() => {
    //         router.push({ name: 'ConnectedAccounts' });
    //       })
    //       .then(() => {
    //         loading.value = false;
    //       });
    //   } catch (error) {
    //     loading.value = false;
    //     console.error('An error occurred:', error);
    //   }
    // };

    onMounted(() => {
      setTimeout(() => {
        const mainElement = document.querySelector('.main');
        if (mainElement) {
          mainElement.scrollTo(0, 0);
        }
      }, 100);

      loading.value = false;
    });

    return {
      store,
      router,
      sliderOptions,
      searchIcons,
      searchTerm,
      searchResults,
      showAutoCategorization,
      showEditSubcategories,
      sliderOptionUpdated,
      editCategorySliderSelected,
      addSubcategory,
      formatMoney,
      iconSelected,
      accountIcon,
      iconColorSelected,
      accountColor,
      parseIconString,
      updateAccount,
      accountName,
      institutionName,
      availableBalance,
      currentBalance,
      accountType,
      removeAccount,
      accountTypesList,
      loading,
      loadingText,
    };
  },
};
</script>

<style scoped>
.cat-info-card {
  padding: 1em;
  border-radius: 5px;
  box-shadow: 0px 20px 20px rgba(0, 0, 0, 0.1);
}

.edit-account-view p {
  margin: 0;
  padding: 0;
}

.light-theme .cat-info-card {
  background: #f8f8f8;
}

.dark-theme .cat-info-card {
  background: #212e3e;
  color: white;
}

.category-color {
  height: 17px;
  width: 17px;
  border-radius: 50%;
}

.info-icon {
  color: #e8f44a;
}
</style> -->
